import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, Link } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { Fade, Stack, Tooltip } from '@mui/material';
import { School } from '@mui/icons-material';
import PanoramaFishEyeIcon from '@mui/icons-material/PanoramaFishEye';
import AdjustIcon from '@mui/icons-material/Adjust';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import SettingsIcon from '@mui/icons-material/Settings';
import logoImg from '/img/logo_color.svg';
import croppedFav from '/img/cropped-fav.png';
import { UserRole } from '@ai/common/src/types/user';
import { getUserStore } from '@ai/common/src/store/UserStore';
import { MenuButton } from '@ai/common/src/sections/main-menu-panel/MenuButton';
import UserMenuButton from '@ai/common/src/sections/main-menu-panel/UserMenuButton';
import { paths } from '@common/paths';
import { APP_WIDTH } from '@App';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BarChartIcon from '@mui/icons-material/BarChart';
import { ActiveTariffRemainings } from './ActiveTariffRemainings';
import ImportContactsIcon from '@mui/icons-material/ImportContacts';
import TelegramIcon from '@mui/icons-material/Telegram';
import QuickreplyIcon from '@mui/icons-material/Quickreply';

interface IMenuItem {
  id: string;
  title: string;
  icon: ReactElement;
  path: string;
}

export const TRANSITION = 'all 0.3s ease';

export const MainMenuPanel = observer(
  ({ isPinned, setIsPinned }: { isPinned: boolean; setIsPinned?: (v: boolean) => void }) => {
    const { t } = useTranslation();
    const location = useLocation();
    const [isCollapsed, setIsCollapsed] = useState(!isPinned);

    const currentUser = getUserStore('platform').currentUser;

    const menuItems: IMenuItem[] = [
      {
        id: 'expert-list',
        title: t('experts'),
        icon: <School sx={{ fontSize: '1.5rem' }} />,
        path: paths.expertList.index,
      },
      {
        id: 'dashboard',
        title: t('dashboard.dashboard'),
        icon: <DashboardIcon sx={{ fontSize: '1.5rem' }} />,
        path: paths.dashboard.index,
      },
      {
        id: 'dialog-history',
        title: t('chatHistory.chatHistory'),
        icon: <ChatBubbleIcon sx={{ fontSize: '1.5rem' }} />,
        path: paths.dialogHistory.index,
      },
      {
        id: 'active-dialogs',
        title: t('chatHistory.activeDialogs'),
        icon: <QuickreplyIcon sx={{ fontSize: '1.5rem' }} />,
        path: paths.activeDialogs.index,
      },
      {
        id: 'pricing',
        title: t('pricing.pricing'),
        icon: <TrendingUpIcon sx={{ fontSize: '1.5rem' }} />,
        path: paths.pricing.index,
      },
      {
        id: 'analytics',
        title: t('pricing.analytics'),
        icon: <BarChartIcon sx={{ fontSize: '1.5rem' }} />,
        path: paths.analytics.index,
      },
      ...(currentUser?.role === UserRole.ADMIN
        ? [
            {
              id: 'users',
              title: t('users.users'),
              icon: <SupervisorAccountIcon sx={{ fontSize: '1.5rem' }} />,
              path: paths.administration.users,
            },
            {
              id: 'settings',
              title: t('settings.settings'),
              icon: <SettingsIcon sx={{ fontSize: '1.5rem' }} />,
              path: paths.administration.settings,
            },
          ]
        : []),
      {
        id: 'docs',
        title: t('docs.docs'),
        icon: <ImportContactsIcon sx={{ fontSize: '1.5rem' }} />,
        path:
          window.location.hostname === 'localhost'
            ? 'http://localhost:3000/doc'
            : `${window.location.origin}/doc`,
      },
    ];

    const page = location.pathname.split('/').pop();

    return (
      <Stack
        position={'relative'}
        width={isPinned ? APP_WIDTH.MENU_TEXT : APP_WIDTH.MENU_ICON + APP_WIDTH.MENU_PADDINGS}
        sx={{ transition: TRANSITION }}
        height="100%"
      >
        <Stack
          height={'100%'}
          maxHeight={'100%'}
          maxWidth={APP_WIDTH.MENU_TEXT}
          minWidth={isCollapsed ? APP_WIDTH.MENU_ICON : APP_WIDTH.MENU_TEXT}
          flexShrink={0}
          px={`${APP_WIDTH.MENU_PADDINGS / 2}px`}
          pb={1}
          zIndex={10}
          borderRight={(theme) => `1px solid ${theme.palette.primary.alpha12}`}
          onMouseEnter={() => (isPinned || !setIsPinned ? undefined : setIsCollapsed(false))}
          onMouseLeave={() => (isPinned || !setIsPinned ? undefined : setIsCollapsed(true))}
          sx={{ transition: TRANSITION, background: 'white' }}
        >
          <Stack
            position={'relative'}
            justifyContent={'center'}
            height={73}
            width={
              isCollapsed ? APP_WIDTH.MENU_ICON : APP_WIDTH.MENU_TEXT - APP_WIDTH.MENU_PADDINGS
            }
            sx={{ transition: TRANSITION, overflow: 'hidden' }}
          >
            <Fade in={!isCollapsed}>
              <img
                src={logoImg}
                alt="logo"
                height={20}
                style={{ position: 'absolute', left: APP_WIDTH.MENU_PADDINGS }}
              />
            </Fade>

            <Fade in={isCollapsed}>
              <img
                src={croppedFav}
                alt="logo"
                height={40}
                style={{ position: 'absolute', margin: '8px' }}
              />
            </Fade>

            <Fade in={!isCollapsed}>
              <Tooltip
                title={isPinned ? t('unpin') : t('pin')}
                sx={{
                  position: 'absolute',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  if (setIsPinned) {
                    setIsPinned(!isPinned);
                    setIsCollapsed(isPinned);
                  }
                }}
              >
                {isPinned ? <AdjustIcon /> : <PanoramaFishEyeIcon />}
              </Tooltip>
            </Fade>
          </Stack>

          <Stack flexGrow={1} gap={1}>
            {menuItems.map((item) => (
              <Link
                to={item.path}
                key={item.id}
                target={item.path.startsWith('http') ? '_blank' : undefined}
              >
                <MenuButton
                  isSmall={isCollapsed}
                  icon={item.icon}
                  text={item.title}
                  active={item.id === page}
                  isExternalLink={item.path.startsWith('http')}
                />
              </Link>
            ))}
          </Stack>

          <Stack gap={1}>
            <Fade in={!isCollapsed}>
              <span>
                <ActiveTariffRemainings />
              </span>
            </Fade>

            <Link to={'https://t.me/NlabAIPlatform'} target={'_blank'}>
              <MenuButton
                isSmall={isCollapsed}
                icon={<TelegramIcon sx={{ fontSize: '1.5rem' }} />}
                text={t('support.support')}
                isExternalLink
              />
            </Link>

            <UserMenuButton isSmall={isCollapsed} frontendId="platform" />
          </Stack>
        </Stack>
      </Stack>
    );
  },
);
